import { useEffect, useMemo } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";

import Login from "./pages/auth/Login";
import RecoverPassword from "./pages/auth/RecoverPassword";
import RegistrationStage1 from "./pages/auth/RegistrationStage1";
import RegistrationStage3 from "./pages/auth/RegistrationStage3";
import Settings from "./pages/auth/Settings";
import FaqPage from "./pages/userPages/FaqPage";
import DataShareInvites from "./pages/userPages/DataShareInvites";
import Assessment from "./pages/userPages/Assessment";
import ErrorPage from "./pages/errorPage/ErrorPage";
import Dashboard from "./pages/userPages/Dashboard";
import SetPassword from "./pages/userPages/SetPassword";
import Sidebar from "./components/layoutComponents/navigation/Sidebar";
import MobileNav from "./components/layoutComponents/navigation/MobileNav";
import * as Constants from "./constants";

// import { fetchContentfulData } from "./store/actions/contentfulDataActions";
// import { fetchContentfulTalentAppData } from "./store/actions/contentfulTalentAppDataActions";
import {
  setTeamData,
  setRequiredAssessments,
} from "./store/actions/teamDataActions";

import { setPoolData } from "./store/actions/poolDataActions";

import { setUserData } from "./store/actions/userDataActions";
import { useDispatch, useSelector } from "react-redux";

import { ThemeProvider } from "@mui/material";
import CacheBuster from "react-cache-buster";
import packageInfo from "../package.json";

import "react-toastify/dist/ReactToastify.css";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";

import TalentsApiService from "./TalentsApiService";
import { countCompletedRequirements, instrumentIds } from "./Utilities";
import { MUITheme } from "./MUITheme";

import { logPageView } from "./analytics";
import {
  getLocaleFromPathname,
  hasInvitationSearch,
  isRestrictedAuthPath,
  stripLocale,
  useLocale,
} from "./locale";
import PublicLocaleGate from "./locale/PublicLocaleGate";
import LegacyPublicRedirect from "./locale/LegacyPublicRedirect";

function App() {
  const userCompletedAssessments = useSelector(
    (state) => state.userData?.all?.completed_assessments
  );
  const teamRequiredAssessments = useSelector(
    (state) => state.teamData?.requiredAssessments
  );
  const poolRequiredAssessments = useSelector(
    (state) => state.poolData?.requiredAssessments
  );
  const allAssessmentsCompleted = useSelector(
    (state) => state.userData?.allAssessmentsCompleted
  );

  const dispatch = useDispatch();
  const { locale, contentRevision } = useLocale();

  const isAuthenticated =
    localStorage.getItem(Constants.MAZHR_JWT_KEY) !== null;

  const isProduction = process.env.NODE_ENV === "production";

  const talentsServiceObj = useMemo(() => {
    return new TalentsApiService();
  }, []);

  /**
   * Invite emails use `?team=` / `?pool=` (and optional `user-token`).
   * `#login` / `#register` are hash fragments, not React Router paths.
   * Locale-prefixed routes still key off query params so `/?team=...#login`
   * is treated as an invitation and is not redirected to login.
   */
  const location = useLocation();
  const pathLocale = getLocaleFromPathname(location.pathname);
  const strippedPath = stripLocale(location.pathname);
  const urlParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search]
  );
  const hasInvitation = hasInvitationSearch(location.search);
  const invitationTeamHash = urlParams?.team;

  useEffect(() => {
    if (hasInvitation) {
      dispatch(
        setUserData({
          invitationData: urlParams,
        })
      );
    }
  }, [hasInvitation, urlParams, dispatch]);

  useEffect(() => {
    logPageView();
    // dispatch(fetchContentfulData());
    // dispatch(fetchContentfulTalentAppData());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      talentsServiceObj.GetCurrentUserDetails((json) => {
        dispatch(setUserData({ all: json }));
        if (json?.linked_teams && json?.linked_teams.length > 0) {
          json.linked_teams.forEach((team) => {
            talentsServiceObj.LoadTeamByHash(team.hash, (teamJson) => {
              dispatch(setTeamData(teamJson));
              if (teamJson?.team?.invite_accepted) {
                dispatch(
                  setRequiredAssessments(teamJson?.team?.required_assessments)
                );
              }
            });
          });
        }

        if (json?.linked_pools && json?.linked_pools.length > 0) {
          json.linked_pools.forEach((pool) => {
            talentsServiceObj.LoadPoolByHash(pool.hash, (poolJson) => {
              dispatch(setPoolData(poolJson));
            });
          });
        }
      });
    } else if (invitationTeamHash) {
      talentsServiceObj.LoadTeamByHash(invitationTeamHash, (teamJson) => {
        dispatch(setTeamData(teamJson));
        dispatch(setRequiredAssessments(teamJson?.team?.required_assessments));
      });
    }
  }, [isAuthenticated, talentsServiceObj, invitationTeamHash, dispatch]);

  useEffect(() => {
    if (userCompletedAssessments) {
      if (Array.isArray(userCompletedAssessments)) {
        const requiredIds = instrumentIds([
          ...(Array.isArray(teamRequiredAssessments) ? teamRequiredAssessments : []),
          ...(Array.isArray(poolRequiredAssessments) ? poolRequiredAssessments : []),
        ]);
        if (requiredIds.length > 0) {
          const { requiredNumber, completedNumber } = countCompletedRequirements(
            requiredIds,
            userCompletedAssessments
          );
          const done = requiredNumber > 0 && completedNumber === requiredNumber;
          dispatch(
            setUserData({
              isLockedFromPrimaryMenu: !done,
              allAssessmentsCompleted: done,
            })
          );
        }
      } else {
        dispatch(
          setUserData({
            isLockedFromPrimaryMenu: false,
            allAssessmentsCompleted: true,
          })
        );
      }
    }
  }, [
    poolRequiredAssessments,
    teamRequiredAssessments,
    userCompletedAssessments,
    dispatch,
  ]);

  if (isAuthenticated && isRestrictedAuthPath(location.pathname)) {
    return (
      <Navigate to={allAssessmentsCompleted ? "/" : "/assessment"} replace />
    );
  }

  const showAppNav =
    isAuthenticated &&
    strippedPath !== "/recover-password" &&
    strippedPath !== "/terms-conditions" &&
    !pathLocale;

  return (
    <CacheBuster
      currentVersion={packageInfo.version}
      isEnabled={isProduction}
      isVerboseMode={false}
    >
      <ThemeProvider theme={MUITheme}>
        <div className={`App locale-${locale}`} data-content-revision={contentRevision}>
          <div className="page">
            {showAppNav && (
              <>{window.innerWidth > 992 ? <Sidebar /> : <MobileNav />}</>
            )}

            <Routes>
              {isAuthenticated && !allAssessmentsCompleted && (
                <Route path="*" element={<Navigate to="assessment" />} />
              )}

              {isAuthenticated && allAssessmentsCompleted && (
                <>
                  <Route path="*" element={<Dashboard />} />
                  <Route path="/" element={<Dashboard />} />
                </>
              )}

              {isAuthenticated && (
                <>
                  <Route path="faq-page" element={<FaqPage />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="assessment" element={<RegistrationStage3 />} />
                  <Route
                    path="data-share-invites"
                    element={<DataShareInvites />}
                  />
                  <Route
                    path="assessment/:assessmentPathName"
                    element={<Assessment />}
                  />
                </>
              )}

              {!isAuthenticated && (
                <>
                  <Route
                    path="login"
                    element={<LegacyPublicRedirect toPath="/login" />}
                  />
                  <Route
                    path="recover-password"
                    element={
                      <LegacyPublicRedirect toPath="/recover-password" />
                    }
                  />
                  <Route
                    path="set-password"
                    element={<LegacyPublicRedirect toPath="/set-password" />}
                  />
                  <Route
                    path="register"
                    element={<LegacyPublicRedirect toPath="/register" />}
                  />

                  <Route path=":locale" element={<PublicLocaleGate />}>
                    <Route path="login" element={<Login />} />
                    <Route
                      path="recover-password"
                      element={<RecoverPassword />}
                    />
                    <Route path="set-password" element={<SetPassword />} />
                    <Route path="register" element={<RegistrationStage1 />} />
                    <Route
                      index
                      element={
                        hasInvitation ? (
                          <RegistrationStage1 />
                        ) : (
                          <LegacyPublicRedirect toPath="/login" />
                        )
                      }
                    />
                    <Route path="*" element={<ErrorPage />} />
                  </Route>

                  <Route
                    path="/"
                    element={
                      <LegacyPublicRedirect
                        toPath={hasInvitation ? "/register" : "/login"}
                      />
                    }
                  />
                </>
              )}

              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </div>
        </div>
      </ThemeProvider>
    </CacheBuster>
  );
}

export default App;
